import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { catchError, map, of } from 'rxjs';
import Swal from 'sweetalert2';
import { environment } from '../../environments/environment';
interface FeedbackItem {
  id: string;
  userId: string;
  type: string;
  description: string;
  datetime: string;
}

type FeedbackItemKey = keyof FeedbackItem;

@Component({
    selector: 'app-datatable',
    imports: [CommonModule, FormsModule],
    templateUrl: './datatable.component.html',
    styleUrls: ['./datatable.component.scss']
})
export class DatatableComponent implements OnInit {
  feedbackData: FeedbackItem[] = [];
  filteredData: FeedbackItem[] = [];
  paginatedData: FeedbackItem[] = [];

  searchTerm: string = '';
  filterCriteria = { type: '' };
  uniqueTypes: string[] = [];

  sortColumn: FeedbackItemKey = 'userId';
  sortDirection: 'asc' | 'desc' = 'asc';

  currentPage: number = 1;
  totalPages: number = 1;
  itemsPerPageOptions = [5, 10, 15];
  itemsPerPage: number = 5;

  constructor(private http: HttpClient, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.fetchFeedbackData();
  }

  fetchFeedbackData() {
    const apiUrl = `${environment.apiBaseUrl}/feedback?db_name=v2`;

    this.http.get<any>(apiUrl).pipe(
      map(response => {
        if (Array.isArray(response)) {
          return response.map((item): FeedbackItem => ({
            id: item.id || '',
            userId: item.userId || '',
            type: item.type || '',
            description: item.description || '',
            datetime: item.datetime || ''
          }));
        } else if (Array.isArray(response.feedbacks)) {
          return response.feedbacks.map((item: any): FeedbackItem => ({
            id: item.id || '',
            userId: item.userId || '',
            type: item.type || '',
            description: item.description || '',
            datetime: item.datetime || ''
          }));
        }
        return [];
      }),
      catchError(error => {        return of([]);
      })
    ).subscribe(data => {
      this.feedbackData = data;
      this.extractUniqueFilters();
      this.applyFilters();
      this.cdr.detectChanges();
    });
  }

  extractUniqueFilters() {
    this.uniqueTypes = Array.from(new Set(this.feedbackData.map(item => item.type))).filter(Boolean);
  }

  sortBy(column: FeedbackItemKey) {
    this.sortDirection = this.sortColumn === column ? (this.sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
    this.sortColumn = column;
    this.applyFilters();
  }

  applyFilters() {
    let data = [...this.feedbackData];

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      data = data.filter(item =>
        item.userId.toLowerCase().includes(term) ||
        item.type.toLowerCase().includes(term) ||
        item.description.toLowerCase().includes(term) ||
        item.datetime.toLowerCase().includes(term) ||
        item.id.toLowerCase().includes(term)
      );
    }

    if (this.filterCriteria.type) {
      data = data.filter(item => item.type === this.filterCriteria.type);
    }

    data.sort((a, b) => {
      const valA = a[this.sortColumn]?.toLowerCase?.() || '';
      const valB = b[this.sortColumn]?.toLowerCase?.() || '';
      return this.sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });

    this.filteredData = data;
    this.currentPage = 1;
    this.updatePagination();
  }

  updatePagination() {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    this.totalPages = Math.ceil(this.filteredData.length / this.itemsPerPage);
    this.paginatedData = this.filteredData.slice(start, end);
  }

  changePage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updatePagination();
  }

  onItemsPerPageChange() {
    this.currentPage = 1;
    this.updatePagination();
  }

 deleteFeedback(id: string) {
  Swal.fire({
    title: 'Are you sure?',
    text: 'Do you really want to delete this feedback? This action cannot be undone.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#5DADE2',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Yes, delete it!'
  }).then((result) => {
    if (result.isConfirmed) {      const deleteUrl = `${environment.apiBaseUrl}/feedback/${id}?db_name=v2`;
      
      this.http.delete(deleteUrl).pipe(
        catchError(error => {          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: 'Failed to delete feedback. Please try again later.'
          });
          return of(null);
        })
      ).subscribe(response => {
        if (response !== null) {
          this.feedbackData = this.feedbackData.filter(item => item.id !== id); 
          this.applyFilters();
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Feedback deleted successfully.'
          });
        }
      });
    }
  });
}

  exportToCSV() {
    if (this.filteredData.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'There is no data to export.'
      });
      return;
    }

    // Define CSV headers
    const headers = ['S.No', 'Email', 'Type', 'Description', 'Datetime', 'ID'];
    
    // Convert data to CSV rows
    const csvRows = this.filteredData.map((item, index) => {
      return [
        index + 1,
        `"${item.userId.replace(/"/g, '""')}"`, // Escape quotes in CSV
        `"${item.type.replace(/"/g, '""')}"`,
        `"${item.description.replace(/"/g, '""')}"`,
        `"${item.datetime}"`,
        `"${item.id}"`
      ].join(',');
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...csvRows].join('\n');

    // Create a Blob and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    // Generate filename with current date
    const date = new Date().toISOString().split('T')[0];
    const filename = `feedback_data_${date}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    Swal.fire({
      icon: 'success',
      title: 'Export Successful',
      text: `Feedback data has been exported to ${filename}`,
      timer: 2000,
      showConfirmButton: false
    });
  }

}
